import type {
  GetPickersResponse,
  GetServicesPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'

import { STEP_COLUMNS, STEP_GUIDANCE } from '../CustomizationFunnel.constants'
import type { Prefixes as PickerPrefixes } from '../components/LargePicker/LargePicker.types'

import { getOptionIcon } from './getOptionIcon'
import { getStepIllustrations } from './getStepIllustrations'
import type { Option, Step } from './types'

export const mapOption = (
  {
    sublabel = undefined,
    ...option
  }:
    | GetPickersResponse['pickerGroups'][number]['items'][number]
    | GetServicesPickersResponse['pickerGroups'][number]['items'][number],
  selectedOptionPriceAmount?: string,
  pickerPrefix: PickerPrefixes = 'circle',
): Option => {
  return {
    ...option,
    priceDiffWithSelected:
      option.price &&
      selectedOptionPriceAmount &&
      String(
        parseInt(option.price.amount, 10) -
          parseInt(selectedOptionPriceAmount, 10),
      ),
    subLabel: sublabel ?? undefined,
    style: {
      prefix: pickerPrefix,
    },
  }
}

export const mapStep = (
  product: GetProductResponse,
  group:
    | GetPickersResponse['pickerGroups'][number]
    | GetServicesPickersResponse['pickerGroups'][number],
): Step => {
  const stepDesktopIllustration = getStepIllustrations(product)

  // Temp tracking that will be removed with no grade
  const gradesGuidanceTracking =
    group.id === 'grades'
      ? { zone: 'grade_picker', name: 'GUARANTEED_FUNCTIONAL' }
      : undefined
  const newBatteryGuidanceTracking =
    group.id === 'battery'
      ? { name: 'NEW_BATTERY', zone: 'battery_picker' }
      : undefined

  const selectedOptionPriceAmount = group.items.find((item) => item.selected)
    ?.price?.amount

  return {
    id: group.id,
    title: group.sectionTitle ?? group.label,
    titleEmphasis: null,
    trackingId: group.trackingId,
    options:
      group.items.map((option) =>
        mapOption(
          option,
          selectedOptionPriceAmount,
          getOptionIcon(group, option),
        ),
      ) ?? [],
    desktopIllustration: stepDesktopIllustration[group.id] ?? null,
    columns: STEP_COLUMNS[group.id] ?? undefined,
    guidance:
      group.guidanceCard && STEP_GUIDANCE[group.id]
        ? {
            modalName: STEP_GUIDANCE[group.id],
            ...(group.guidanceCard.icon?.name
              ? { icon: group.guidanceCard.icon.name }
              : {}),
            ...(group.guidanceCard.icon?.url
              ? { image: group.guidanceCard.icon.url }
              : {}),
            label: group.guidanceCard.text,
            clickable: group.guidanceCard.clickable || false,
            tracking:
              gradesGuidanceTracking ?? newBatteryGuidanceTracking ?? undefined,
          }
        : undefined,
    sideContent: group.sideContent,
  } satisfies Step
}

export const mapServiceStep = (
  product: GetProductResponse,
  group: GetServicesPickersResponse['pickerGroups'][number],
): Step => {
  return {
    type: 'service',
    ...mapStep(product, group),
  }
}
